import { defineStore } from 'pinia'
import { useSession } from './session.js'

const KEY = 'addcrm:privacy'
const SIZE_KEY = 'addcrm:privacy-spot'
// A mode chosen here not yet on the account (offline, refused, or the page reloaded first).
const UNSYNCED_KEY = 'addcrm:privacy-unsynced'
const COVERED_KEY = 'addcrm:privacy-covered'
const read = (key = KEY) => {
  try {
    return localStorage.getItem(key) || null
  } catch {
    return null
  }
}
const write = (key, value) => {
  try {
    if (value === null || value === false) localStorage.removeItem(key)
    else localStorage.setItem(key, String(value))
  } catch {
    // private window: this session only
  }
}

/** The spot's sizes, half-width × half-height in pixels: from a few pixels (about a letter) to a few lines. */
export const SPOT_SIZES = [[6, 6], [9, 8], [13, 10], [18, 12], [25, 14], [34, 16], [48, 18], [72, 22], [100, 30], [140, 44], [190, 64]]
const DEFAULT_SIZE = 1

/**
 * Working in public (D-260): the mode — none, 'discreet' (the sensitive values masked, a value
 * shown while pointed at or held), 'full' (all masked but the row or field under the pointer and
 * the one being typed in), 'spot' (all masked but a few pixels around the pointer, or the text
 * cursor while typing), 'discreet_spot' (the sensitive values seen only through those few
 * pixels, the rest readable) — kept with the person's settings, so every device of theirs and their
 * pushes follow; on this device at once, before the session comes, offline, and across a reload
 * (F5): what was chosen here and has not reached the account yet wins over the account's. The
 * screen covered stays covered across a reload too.
 */
export const usePrivacy = defineStore('privacy', {
  // The spot's size: chosen on each device (a phone and a large screen want different ones).
  state: () => ({ mode: read(), covered: read(COVERED_KEY) === '1' && !!read(), spotSize: Math.min(SPOT_SIZES.length - 1, Math.max(0, Number(read(SIZE_KEY) ?? DEFAULT_SIZE) || 0)) }),
  getters: {
    on: (s) => !!s.mode,
  },
  actions: {
    apply() {
      const root = document.documentElement
      root.classList.toggle('pv-discreet', this.mode === 'discreet')
      root.classList.toggle('pv-full', this.mode === 'full')
      root.classList.toggle('pv-spot', this.mode === 'spot')
      root.classList.toggle('pv-dspot', this.mode === 'discreet_spot')
      write(KEY, this.mode)
    },
    /**
     * The mode the person's settings say (another device may have changed it) — unless the one
     * chosen here has not reached the account yet: then it is sent again, and stays.
     */
    adopt(mode) {
      if (read(UNSYNCED_KEY)) return this.sync()
      if ((mode || null) === this.mode) return
      this.mode = mode || null
      this.apply()
    },
    async set(mode) {
      this.mode = mode || null
      this.apply()
      if (!this.mode) this.uncover()
      write(UNSYNCED_KEY, '1')
      await this.sync()
    },
    async sync() {
      try {
        await useSession().updateSettings({ privacy_mode: this.mode })
        write(UNSYNCED_KEY, null)
      } catch {
        // offline or refused: kept here, sent with the next start
      }
    },
    /**
     * The shortcut starts from the strictest, so one key hides everything at once when somebody
     * looks: off → spot; then easing off, spot → full → discreet + spot → discreet → off.
     */
    cycle() {
      return this.set({ null: 'spot', spot: 'full', full: 'discreet_spot', discreet_spot: 'discreet', discreet: null }[String(this.mode)])
    },
    /** The spot bigger (by > 0) or smaller, within its sizes. */
    resizeSpot(by) {
      this.spotSize = Math.min(SPOT_SIZES.length - 1, Math.max(0, this.spotSize + by))
      try {
        localStorage.setItem(SIZE_KEY, String(this.spotSize))
      } catch {
        // private window: this session only
      }
    },
    cover() {
      this.covered = true
      write(COVERED_KEY, '1')
    },
    uncover() {
      this.covered = false
      write(COVERED_KEY, null)
    },
  },
})
